import { useState, type FormEvent } from 'react';

export type SortOrder = 'asc' | 'desc';

/**
 * Draft/applied filters + page + sort state shared by admin list pages.
 * Sort order flips on every header click (legacy `SortItem` behaviour), starting at `desc`.
 */
export function useListFilters<F extends object>(emptyFilters: F) {
  const [draft, setDraft] = useState<F>(emptyFilters);
  const [applied, setApplied] = useState<F>(emptyFilters);
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const setField = <K extends keyof F>(key: K, value: F[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const patchDraft = (patch: Partial<F>) => setDraft((prev) => ({ ...prev, ...patch }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setPage(1);
    setApplied(draft);
  };

  const reset = (e: FormEvent) => {
    e.preventDefault();
    setDraft(emptyFilters);
    setApplied(emptyFilters);
    setPage(1);
  };

  const toggleSort = (field: string) => {
    setSortField(field);
    setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  };

  return {
    draft,
    applied,
    page,
    sortField,
    sortOrder,
    setField,
    patchDraft,
    setPage,
    submit,
    reset,
    toggleSort,
  };
}
